import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, XMarkIcon, TrashIcon, PlusIcon, ServerStackIcon, ShieldCheckIcon, CloudArrowUpIcon, CloudArrowDownIcon, InformationCircleIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import ContextTabLink from './common/ContextTabLink';
import ServiceOverviewMap from './ServiceOverviewMap';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import '../styles/DetailView.css';
import './ServerFormCards.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';

const EMPTY = {
  name: '',
  software_service_id: '',
  schedule: '',
  schedule_frequency: '',
  schedule_weekdays: [],
  schedule_time: '',
  schedule_day_of_month: '',
  schedule_interval_hours: '',
  schedule_window_start: '',
  schedule_window_end: '',
  retention_days: '',
  backup_type: '',
  job_type: 'backup',
  replica_host_type: '',
  replica_host_id: '',
  copy_trigger: '',
  source_policy_id: '',
  destination_type: '',
  destination_detail: '',
  repository_type: '',
  repository_id: '',
  encryption: false,
  is_offsite: false,
  offsite_detail: '',
  offsite_repository_type: '',
  offsite_repository_id: '',
  status: 'active',
  last_run_at: '',
  last_run_status: '',
  notes: '',
};

const WEEKDAY_SHORT = { 1: 'L', 2: 'M', 3: 'X', 4: 'J', 5: 'V', 6: 'S', 7: 'D' };
const CRON_WEEKDAY = { 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '0' };

const FALLBACK_WEEKDAYS = [
  { value: 1, label: 'Lunes' }, { value: 2, label: 'Martes' }, { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' }, { value: 5, label: 'Viernes' }, { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
];

const FALLBACK_FREQUENCIES = [
  { value: 'daily', label: 'Todos los días' },
  { value: 'weekly', label: 'Días de la semana' },
  { value: 'monthly', label: 'Día del mes' },
  { value: 'interval', label: 'Cada N horas (dentro de una ventana)' },
  { value: 'custom', label: 'Personalizada (cron)' },
];

const FALLBACK_JOB_TYPES = [
  { value: 'backup', label: 'Copia de seguridad (backup)' },
  { value: 'replica', label: 'Réplica (a otro host / standby)' },
  { value: 'copy', label: 'Backup Copy (copia de los backups)' },
];

const FALLBACK_COPY_TRIGGERS = [
  { value: 'after_backup', label: 'Al terminar el backup del que copia' },
  { value: 'schedule', label: 'Con su propia programación (independiente)' },
  { value: 'continuous', label: 'Continuo (en cuanto hay copias nuevas)' },
];

const FALLBACK_REPOSITORY_TYPES = [
  { value: 'server', label: 'Servidor / NAS físico' },
  { value: 'vm', label: 'Máquina virtual' },
  { value: 'service', label: 'Servicio (almacenamiento o nube)' },
  { value: 'farm', label: 'Granja de servicios' },
  { value: 'server_volume', label: 'Volumen de un equipo (letra/unidad)' },
  { value: 'storage_lun', label: 'LUN de cabina (iSCSI / FC)' },
];

const FALLBACK_DESTINATION_TYPES = [
  { value: 'local', label: 'Disco local de la máquina' },
  { value: 'nas', label: 'NAS / recurso compartido (SMB-NFS)' },
  { value: 'san', label: 'SAN (iSCSI / FC)' },
  { value: 'tape', label: 'Cinta (LTO)' },
  { value: 'cloud_s3', label: 'Nube S3 (AWS, MinIO, Wasabi…)' },
  { value: 'cloud_azure', label: 'Azure Blob Storage' },
  { value: 'cloud_gcs', label: 'Google Cloud Storage' },
  { value: 'cloud_other', label: 'Otra nube / proveedor' },
];

const helpBox = {
  background: 'rgba(127,127,127,0.10)',
  border: '1px solid rgba(127,127,127,0.25)',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: '0.8rem',
  lineHeight: 1.45,
};

/** Vista previa del cron que va a guardar el backend. */
function buildCronPreview(frequency, weekdays, time, dayOfMonth, custom, intervalHours, windowStart, windowEnd) {
  if (frequency === 'custom') return (custom || '').trim();
  if (frequency === 'interval') {
    const s = /^(\d{1,2}):(\d{2})$/.exec((windowStart || '').trim());
    const e = /^(\d{1,2}):(\d{2})$/.exec((windowEnd || '').trim());
    const step = Number(intervalHours);
    if (!s || !e || !step || step < 1) return '';
    const hour = Number(s[1]);
    const last = Number(e[1]) - 1 >= hour ? Number(e[1]) - 1 : Number(e[1]);
    if (last < hour) return '';
    const days = [...weekdays].sort((a, b) => a - b);
    return `${Number(s[2])} ${hour}-${last}/${step} * * ${days.length ? days.map(d => CRON_WEEKDAY[d]).join(',') : '*'}`;
  }
  const match = /^(\d{1,2}):(\d{2})$/.exec((time || '').trim());
  if (!match) return '';
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return '';

  if (frequency === 'daily') return `${minute} ${hour} * * *`;
  if (frequency === 'weekly') {
    const days = [...weekdays].sort((a, b) => a - b);
    if (!days.length) return '';
    return `${minute} ${hour} * * ${days.map(d => CRON_WEEKDAY[d]).join(',')}`;
  }
  if (frequency === 'monthly') {
    const dom = Number(dayOfMonth);
    if (!dom || dom < 1 || dom > 31) return '';
    return `${minute} ${hour} ${dom} * *`;
  }
  return '';
}

export default function BackupPolicyForm({ policy, companyId, meta = {}, services = [], policies = [], onClose, onSaved, initialMode, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  /* La ficha se abre EN LECTURA (clic en la fila del listado); 'create' y 'edit'
     editan. Sin `initialMode` edita, como siempre. */
  const [editing, setEditing] = useState(initialMode ? initialMode !== 'view' : true);
  const isEdit = Boolean(policy?.id);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [targets, setTargets] = useState([]);
  const [repositories, setRepositories] = useState([]);
  const [softwareService, setSoftwareService] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [targetForm, setTargetForm] = useState({ target_type: 'server', target_id: '', notes: '' });
  const [targetSaving, setTargetSaving] = useState(false);
  // En lectura se abre en «Resumen» (servicio, targets, destino e impacto);
  // al editar se abre en «Programación».
  const [activeTab, setActiveTab] = useState(policy?.id && initialMode === 'view' ? 'resumen' : 'programacion');
  const [showTypeHelp, setShowTypeHelp] = useState(false);

  useEffect(() => {
    if (policy?.id && initialMode === 'view') setActiveTab('resumen');
  }, [policy?.id, initialMode]);

  const backupTypes = useMemo(
    () => meta.backup_type_catalog || (meta.backup_types || []).map(t => ({ value: t, label: t, description: null })),
    [meta],
  );
  const frequencies = meta.schedule_frequencies?.length ? meta.schedule_frequencies : FALLBACK_FREQUENCIES;
  const weekdays = meta.weekdays?.length ? meta.weekdays : FALLBACK_WEEKDAYS;
  const repositoryTypes = meta.repository_types?.length ? meta.repository_types : FALLBACK_REPOSITORY_TYPES;
  const jobTypes = meta.job_types?.length ? meta.job_types : FALLBACK_JOB_TYPES;
  const copyTriggers = meta.copy_triggers?.length ? meta.copy_triggers : FALLBACK_COPY_TRIGGERS;
  const destinationTypes = meta.destination_type_catalog
    || (meta.destination_types || []).map(t => ({ value: t, label: t }));
  const destinationOptions = destinationTypes.length ? destinationTypes : FALLBACK_DESTINATION_TYPES;

  // Los repositorios son CIs reales de la CMDB: no se teclean, se eligen.
  useEffect(() => {
    if (!companyId) return;
    let alive = true;
    api.getBackupRepositories(companyId)
      .then(res => { if (alive) setRepositories(res.items || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [companyId]);

  // Detalle del servicio de backup enlazado: de ahí salen host, relaciones y
  // el mapa de dependencias del que habla el resumen.
  useEffect(() => {
    if (!companyId || !form.software_service_id) { setSoftwareService(null); return undefined; }
    let alive = true;
    api.getService(form.software_service_id, companyId)
      .then(svc => { if (alive) setSoftwareService(svc); })
      .catch(() => { if (alive) setSoftwareService(null); });
    return () => { alive = false; };
  }, [companyId, form.software_service_id]);

  useEffect(() => {
    if (policy) {
      setForm({
        ...EMPTY,
        name: policy.name || '',
        software_service_id: policy.software_service_id || '',
        schedule: policy.schedule || '',
        schedule_frequency: policy.schedule_frequency || '',
        schedule_weekdays: Array.isArray(policy.schedule_weekdays)
          ? policy.schedule_weekdays
          : String(policy.schedule_weekdays || '').split(',').map(Number).filter(Boolean),
        schedule_time: policy.schedule_time || '',
        schedule_day_of_month: policy.schedule_day_of_month || '',
        schedule_interval_hours: policy.schedule_interval_hours || '',
        schedule_window_start: policy.schedule_window_start || '',
        schedule_window_end: policy.schedule_window_end || '',
        retention_days: policy.retention_days || '',
        backup_type: policy.backup_type || '',
        job_type: policy.job_type || 'backup',
        replica_host_type: policy.replica_host_type || '',
        replica_host_id: policy.replica_host_id || '',
        copy_trigger: policy.copy_trigger || '',
        source_policy_id: policy.source_policy_id || '',
        destination_type: policy.destination_type || '',
        destination_detail: policy.destination_detail || '',
        repository_type: policy.repository_type || '',
        repository_id: policy.repository_id || '',
        encryption: Boolean(policy.encryption),
        is_offsite: Boolean(policy.is_offsite),
        offsite_detail: policy.offsite_detail || '',
        offsite_repository_type: policy.offsite_repository_type || '',
        offsite_repository_id: policy.offsite_repository_id || '',
        status: policy.status || 'active',
        last_run_at: policy.last_run_at || '',
        last_run_status: policy.last_run_status || '',
        notes: policy.notes || '',
      });
      setTargets(policy.targets || []);
    } else {
      setForm(EMPTY);
      setTargets([]);
    }
    setIsDirty(false);
  }, [policy, reloadKey]);


  useEffect(() => {
    if (!isEdit || !companyId) return;
    api.getBackupPolicy(policy.id, companyId).then(fresh => {
      setTargets(fresh.targets || []);
    }).catch(() => {});
  }, [isEdit, companyId, policy?.id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setIsDirty(true);
    setError('');
  };

  /* Descartar: recarga los valores guardados y sale de edición. */
  const handleDiscard = () => {
    setReloadKey(k => k + 1);
    setIsDirty(false);
    setError('');
    if (isEdit) setEditing(false);
  };

  const toggleWeekday = (day) => {
    setError('');
    setIsDirty(true);
    setForm(prev => ({
      ...prev,
      schedule_weekdays: prev.schedule_weekdays.includes(day)
        ? prev.schedule_weekdays.filter(d => d !== day)
        : [...prev.schedule_weekdays, day].sort((a, b) => a - b),
    }));
  };

  const repoValue = (type, id) => (type && id ? `${type}:${id}` : '');

  /** El repositorio se elige de la CMDB; el tipo de destino se sugiere solo. */
  const handleRepositoryChange = (field, rawValue) => {
    setError('');
    setIsDirty(true);
    const [type, id] = rawValue ? rawValue.split(':') : ['', ''];
    setForm(prev => {
      const next = { ...prev, [`${field}_type`]: type, [`${field}_id`]: id || '' };
      if (field === 'repository' && type) {
        const item = repositories.find(
          r => r.repository_type === type && String(r.repository_id) === String(id),
        );
        if (item?.suggested_destination_type && !prev.destination_type) {
          next.destination_type = item.suggested_destination_type;
        }
      }
      if (field === 'offsite_repository' && type) next.is_offsite = true;
      return next;
    });
  };

  const selectedRepository = (type, id) => repositories.find(
    r => r.repository_type === type && String(r.repository_id) === String(id),
  );

  const TARGET_LABELS = {
    server: 'Servidor / NAS', rack_element: 'Servidor',
    vm: 'Máquina virtual', virt_vm: 'Máquina virtual',
    service: 'Servicio', farm: 'Granja', service_farm: 'Granja',
    workstation: 'Puesto', storage_lun: 'LUN', server_volume: 'Volumen',
  };
  const targetTypeLabel = (t) => TARGET_LABELS[t] || repositoryTypes.find(rt => rt.value === t)?.label || t || '—';
  const targetRoute = (t) => {
    if (!t?.target_id) return null;
    if (t.target_type === 'service') return `/services?destacar=${t.target_id}&abrir=1`;
    if (t.target_type === 'virt_vm' || t.target_type === 'vm') return `/virt/vms?destacar=${t.target_id}`;
    if (t.target_type === 'rack_element' || t.target_type === 'server') return `/infrastructure/servers?destacar=${t.target_id}`;
    return null;
  };

  const jobTypeInfo = jobTypes.find(j => j.value === form.job_type);
  const softwareInfo = softwareService
    || services.find(s => String(s.id) === String(form.software_service_id))
    || null;
  const softwareName = softwareInfo?.display_name || softwareInfo?.name
    || (form.software_service_id ? `Servicio #${form.software_service_id}` : '');
  const softwareHost = softwareInfo?.host_ci_name
    || softwareInfo?.host_vm_name
    || softwareInfo?.host_server_name
    || (softwareInfo?.host_ci_type && softwareInfo?.host_ci_id
      ? `${softwareInfo.host_ci_type} #${softwareInfo.host_ci_id}`
      : '');
  const repositoryName = selectedRepository(form.repository_type, form.repository_id)?.name
    || form.destination_detail || '';
  const replicaName = selectedRepository(form.replica_host_type, form.replica_host_id)?.name || '';
  const offsiteName = selectedRepository(form.offsite_repository_type, form.offsite_repository_id)?.name
    || form.offsite_detail || '';
  const targetCounts = targets.reduce((acc, t) => {
    const label = targetTypeLabel(t.target_type);
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});
  const TARGET_PLURALS = {
    'Máquina virtual': 'máquinas virtuales',
    'Servidor': 'servidores',
    'Servidor / NAS': 'servidores/NAS',
    'Servicio': 'servicios',
    'Granja': 'granjas',
    'Puesto': 'puestos',
    'LUN': 'LUNs',
    'Volumen': 'volúmenes',
  };
  const targetSummary = Object.entries(targetCounts)
    .map(([label, count]) => `${count} ${count === 1 ? label.toLowerCase() : (TARGET_PLURALS[label] || `${label.toLowerCase()}s`)}`)
    .join(' · ');
  const statusLabel = ({ active: 'Activa', disabled: 'Desactivada', error: 'Con error' }[form.status] || form.status || '—');
  // La copia copia otro trabajo: se eligen entre las demás políticas.
  const otherPolicies = policies.filter(p => String(p.id) !== String(policy?.id));

  // Solo los servicios marcados como "Software de backup" tienen sentido aquí.
  const backupSoftware = useMemo(
    () => services.filter(s => s.service_type === 'backup_software'),
    [services],
  );
  const currentSoftware = services.find(s => String(s.id) === String(form.software_service_id));
  const softwareNotBackup = Boolean(currentSoftware && currentSoftware.service_type !== 'backup_software');

  const selectedType = backupTypes.find(t => t.value === form.backup_type);
  const cronPreview = buildCronPreview(
    form.schedule_frequency,
    form.schedule_weekdays,
    form.schedule_time,
    form.schedule_day_of_month,
    form.schedule,
    form.schedule_interval_hours,
    form.schedule_window_start,
    form.schedule_window_end,
  );
  const scheduleSummary = (() => {
    const time = form.schedule_time ? ` a las ${form.schedule_time}` : '';
    if (form.schedule_frequency === 'daily') return `Diaria${time}`;
    if (form.schedule_frequency === 'weekly') {
      const days = (form.schedule_weekdays || []).map(d => WEEKDAY_SHORT[d] || d).join(' ');
      return `Semanal${days ? ` (${days})` : ''}${time}`;
    }
    if (form.schedule_frequency === 'monthly') {
      return `Día ${form.schedule_day_of_month || '—'} de cada mes${time}`;
    }
    if (form.schedule_frequency === 'interval') {
      return `Cada ${form.schedule_interval_hours || '—'} h (${form.schedule_window_start || '—'}–${form.schedule_window_end || '—'})`;
    }
    if (form.schedule_frequency === 'custom') return form.schedule ? `Cron ${form.schedule}` : 'Cron personalizado';
    return form.schedule ? `Cron ${form.schedule}` : 'Sin programar';
  })();

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setError('');
    try {
      if (!form.name.trim()) throw new Error('El nombre es obligatorio');
      if (form.schedule_frequency === 'weekly' && !form.schedule_weekdays.length) {
        throw new Error('Selecciona al menos un día de la semana');
      }
      if (form.schedule_frequency === 'monthly' && !form.schedule_day_of_month) {
        throw new Error('Indica el día del mes (1-31)');
      }
      if (form.schedule_frequency === 'interval') {
        if (!form.schedule_interval_hours) throw new Error('Indica cada cuántas horas se ejecuta');
        if (!form.schedule_window_start || !form.schedule_window_end) {
          throw new Error('Indica la ventana horaria (de … a …)');
        }
      }
      if (form.schedule_frequency && form.schedule_frequency !== 'custom'
        && form.schedule_frequency !== 'interval' && !form.schedule_time) {
        throw new Error('Indica la hora de inicio (HH:MM)');
      }
      if (form.job_type === 'replica' && form.replica_host_type && !form.replica_host_id) {
        throw new Error('Elige el host donde se exponen las réplicas o déjalo sin enlazar');
      }
      if (form.repository_type && !form.repository_id) {
        throw new Error('Elige el elemento que hace de repositorio o déjalo sin enlazar');
      }
      if (form.offsite_repository_type && !form.offsite_repository_id) {
        throw new Error('Elige el repositorio offsite o déjalo sin enlazar');
      }

      setSaving(true);
      const payload = {
        ...form,
        software_service_id: form.software_service_id ? Number(form.software_service_id) : null,
        retention_days: form.retention_days === '' ? null : Number(form.retention_days),
        schedule_day_of_month: form.schedule_day_of_month === '' ? null : Number(form.schedule_day_of_month),
        schedule_interval_hours: form.schedule_interval_hours === '' ? null : Number(form.schedule_interval_hours),
        schedule_window_start: form.schedule_window_start || null,
        schedule_window_end: form.schedule_window_end || null,
        job_type: form.job_type || 'backup',
        replica_host_type: form.replica_host_type || null,
        replica_host_id: form.replica_host_id ? Number(form.replica_host_id) : null,
        source_policy_id: form.source_policy_id ? Number(form.source_policy_id) : null,
        repository_id: form.repository_id ? Number(form.repository_id) : null,
        offsite_repository_id: form.offsite_repository_id ? Number(form.offsite_repository_id) : null,
      };
      let creado = null;
      if (isEdit) await api.updateBackupPolicy(policy.id, payload, companyId);
      else creado = registroGuardado(await api.createBackupPolicy(payload, companyId));
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las zonas que necesitan estar guardado.
      onSaved?.(creado);
      setIsDirty(false);
      return true;
    } catch (err) {
      setError(err.message || 'No se pudo guardar la política');
      return false;
    } finally { setSaving(false); }
  };

  const addTarget = async (e) => {
    e.preventDefault();
    if (!isEdit) return;
    if (!targetForm.target_id) { setError('Selecciona qué se protege'); return; }
    setTargetSaving(true); setError('');
    try {
      const payload = { ...targetForm };
      payload.target_id = Number(payload.target_id);
      const created = await api.addBackupTarget(policy.id, payload, companyId);
      setTargets(prev => [...prev, created]);
      setTargetForm({ target_type: 'service', target_id: '', notes: '' });
    } catch (err) {
      setError(err.message || 'No se pudo añadir el target');
    } finally { setTargetSaving(false); }
  };

  const deleteTarget = async (tid) => {
    if (!window.confirm('¿Eliminar este target?')) return;
    try {
      await api.deleteBackupTarget(policy.id, tid, companyId);
      setTargets(prev => prev.filter(t => t.id !== tid));
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el target');
    }
  };

  const tabsNav = (
    <div className="dv-tabs-nav">
      {isEdit && (
        <TabButton type="button" className={`dv-tab-btn${activeTab === 'resumen' ? ' active' : ''}`} onClick={() => setActiveTab('resumen')}>Resumen</TabButton>
      )}
      <TabButton type="button" className={`dv-tab-btn${activeTab === 'programacion' ? ' active' : ''}`} onClick={() => setActiveTab('programacion')}>Programación</TabButton>
      <TabButton type="button" className={`dv-tab-btn${activeTab === 'destino' ? ' active' : ''}`} onClick={() => setActiveTab('destino')}>Destino</TabButton>
      {isEdit && (
        <TabButton type="button" className={`dv-tab-btn${activeTab === 'targets' ? ' active' : ''}`} onClick={() => setActiveTab('targets')}>Targets ({targets.length})</TabButton>
      )}
      {isEdit && form.software_service_id && (
        <TabButton type="button" className={`dv-tab-btn${activeTab === 'dependencias' ? ' active' : ''}`} onClick={() => setActiveTab('dependencias')}>Dependencias</TabButton>
      )}
      {isEdit && (
        <TabButton type="button" className={`dv-tab-btn${activeTab === 'historial' ? ' active' : ''}`} onClick={() => setActiveTab('historial')}>Historial</TabButton>
      )}
    </div>
  );
  const paneClass = `dv-tab-pane${isCards ? ' sf2-pane' : ''}`;

  return (
    <div className="dv-overlay">
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={onClose}>
          <ArrowLeftIcon />
          Políticas de backup
        </button>
        <DetailCrumb label={form.name || 'Nueva'} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Política"
          disabled={saving}
          onChange={(destino) => {
            if (!editing || window.confirm('Hay una edición abierta. ¿Descartar los cambios sin guardar y pasar a la siguiente política?')) {
              onNavigate?.(destino);
            }
          }}
        />
        <div className="dv-topbar-spacer" />
        {error && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>⚠ {error}</span>}
        <div className="dv-topbar-actions">
          {isCards ? (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
              <XMarkIcon />
              Cerrar
            </button>
          ) : !editing ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon />
                Cerrar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={() => { setEditing(true); setActiveTab('programacion'); }}>
                <CheckIcon />
                Editar
              </button>
            </>
          ) : (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose} disabled={saving}>
                <XMarkIcon />
                Descartar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={handleSave} disabled={saving}>
                {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
        </div>
      </div>

      {!isCards && (
      <div className="dv-smartbar">
        {isEdit && (
          <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('targets')}>
            <div className="dv-smart-btn-icon"><ServerStackIcon /></div>
            <div className="dv-smart-btn-value">{targets.length}</div>
            <div className="dv-smart-btn-label">Targets</div>
          </button>
        )}        <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('destino')}>
          <div className="dv-smart-btn-icon"><ShieldCheckIcon /></div>
          <div className="dv-smart-btn-value">{form.encryption ? '✓' : '✗'}</div>
          <div className="dv-smart-btn-label">Cifrado</div>
        </button>
        <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('destino')}>
          <div className="dv-smart-btn-icon"><CloudArrowUpIcon /></div>
          <div className="dv-smart-btn-value">{form.is_offsite ? '✓' : '✗'}</div>
          <div className="dv-smart-btn-label">Offsite</div>
        </button>
        {isEdit && form.software_service_id && (
          <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('dependencias')}
            title="Servicio de backup, dependencias e impacto">
            <div className="dv-smart-btn-icon"><Squares2X2Icon /></div>
            <div className="dv-smart-btn-value">{form.software_service_id ? '🔗' : '—'}</div>
            <div className="dv-smart-btn-label">Dependencias</div>
          </button>
        )}

        {isEdit && (
          <DocSmartButton elementType="backup_policy" elementId={policy.id} elementName={form.name} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── (en lectura se apaga de golpe con el fieldset) ── */}
      <div className="dv-body">
        {/* El fieldset NO puede ser hijo directo de .dv-body con `display:
            contents`: aplanaría el formulario, los targets y el alta de target
            como columnas hermanas y la ficha se partía en tres. */}
        <div className={`dv-main${isCards ? ' sf2-main' : ''}`}>

        <FormCardsShell
          isCards={isCards}
          header={isCards ? (
            <FormCardsHead
              icon={<CloudArrowDownIcon style={{ width: 20, height: 20 }} />}
              title={form.name}
              onTitleChange={(v) => { setForm(prev => ({ ...prev, name: v })); setIsDirty(true); }}
              placeholder="Nombre de la política"
              editing={editing}
              saving={saving}
              onSave={handleSave}
              onCancel={onClose}
              onEdit={() => setEditing(true)}
              crumb={[form.job_type, form.storage_name, form.software_service_id ? 'con servicio' : null].filter(Boolean).join(' › ')}
              badges={[
                ...(form.schedule_type ? [{ text: form.schedule_type }] : []),
                ...(form.encryption ? [{ text: 'Cifrado', color: '#16a34a' }] : []),
                ...(form.is_offsite ? [{ text: 'Offsite', color: '#0369a1' }] : []),
              ]}
              chips={[
                { value: targets.length, label: 'targets', onClick: () => setActiveTab('targets') },
                ...(form.retention_days ? [{ value: `${form.retention_days} d`, label: 'retención' }] : []),
              ]}
              extra={isEdit ? (
                <DocSmartButton elementType="backup_policy" elementId={policy.id} elementName={form.name} companyId={companyId} variant="chip" />
              ) : null}
            />
          ) : null}
          tabs={isCards ? tabsNav : null}
        >
        <fieldset className="dv-fieldset" disabled={!editing}>
        <form className="dv-form" onSubmit={handleSave}>
          <input className="dv-record-title" name="name" value={form.name} onChange={handleChange} placeholder="Nombre de la política" />

          {!isCards && tabsNav}

          {activeTab === 'resumen' && isEdit && (
            <div className={paneClass}>
              {isCards && (
                <div className="sf2-hl" style={{ marginBottom: 12 }}>
                  <div className="sf2-kpi"><span>Trabajo</span><b style={{ fontSize: '0.95rem' }}>{form.job_type || '—'}</b></div>
                  <div className="sf2-kpi"><span>Programación</span><b style={{ fontSize: '0.95rem' }}>{form.schedule_type || '—'}</b></div>
                  <div className="sf2-kpi"><span>Retención</span><b style={{ fontSize: '0.95rem' }}>{form.retention_days ? `${form.retention_days} d` : '—'}</b></div>
                  <div className="sf2-kpi"><span>Targets</span><b>{targets.length}</b></div>
                  <div className="sf2-kpi"><span>Cifrado</span><b>{form.encryption ? '✓' : '✗'}</b></div>
                </div>
              )}
              <div className="dv-context-grid">
                <div className="dv-context-card">
                  <div className="dv-context-icon">🛡️</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Servicio de backup</div>
                    <div className="dv-context-title">{softwareName || 'Sin software enlazado'}</div>
                    <div className="dv-context-sub">
                      {softwareHost ? `Ejecuta en ${softwareHost}` : 'Sin host enlazado'}
                    </div>
                    {form.software_service_id && (
                      <div className="dv-context-actions">
                        <Link to={`/services?destacar=${form.software_service_id}&abrir=1`} className="dv-context-link">
                          Abrir servicio ↗
                        </Link>
                        <Link to={`/services/designer?service=${form.software_service_id}`} className="dv-context-link">
                          Diseñador ↗
                        </Link>
                      </div>
                    )}
                  </div>
                </div>

                <div className="dv-context-card">
                  <div className="dv-context-icon">🎯</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Protege</div>
                    <div className="dv-context-title">{targets.length} elemento{targets.length === 1 ? '' : 's'}</div>
                    <div className="dv-context-sub">{targetSummary || 'Sin targets todavía'}</div>
                    {targets.length > 0 && (
                      <ul className="dv-context-list">
                        {targets.slice(0, 4).map(t => {
                          const route = targetRoute(t);
                          return (
                            <li key={t.id}>
                              {route ? (
                                <Link to={route} className="dv-context-link">{t.target_name || `#${t.target_id}`}</Link>
                              ) : (t.target_name || `#${t.target_id}`)}
                            </li>
                          );
                        })}
                        {targets.length > 4 && <li>+{targets.length - 4} más…</li>}
                      </ul>
                    )}
                    <div className="dv-context-actions">
                      <ContextTabLink tab="targets" onSelect={setActiveTab}>Ver targets</ContextTabLink>
                    </div>
                  </div>
                </div>

                <div className="dv-context-card">
                  <div className="dv-context-icon">💾</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Dónde guarda</div>
                    <div className="dv-context-title">{repositoryName || 'Sin repositorio enlazado'}</div>
                    <div className="dv-context-sub">{replicaName ? `Réplica: ${replicaName}` : 'Sin host de réplica'}</div>
                    <div className="dv-context-sub">{offsiteName ? `Offsite: ${offsiteName}` : 'Sin copia offsite'}</div>
                    <div className="dv-context-actions">
                      <ContextTabLink tab="destino" onSelect={setActiveTab}>Ver destino</ContextTabLink>
                    </div>
                  </div>
                </div>

                <div className="dv-context-card dv-context-card-warn">
                  <div className="dv-context-icon">⚠️</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Impacto</div>
                    <div className="dv-context-title">
                      {targets.length ? `${targets.length} elemento${targets.length === 1 ? '' : 's'} sin copia si falla` : 'Sin elementos protegidos'}
                    </div>
                    <div className="dv-context-sub">
                      {softwareName ? `Se ejecuta desde ${softwareName}${softwareHost ? ` (${softwareHost})` : ''}.` : 'Sin servicio de backup enlazado.'}
                    </div>
                    <div className="dv-context-actions">
                      {form.software_service_id && (
                        <ContextTabLink tab="dependencias" onSelect={setActiveTab}>Ver dependencias ↗</ContextTabLink>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Resumen de la política</div>
                <div className="dv-summary-grid">
                  <div><span>Tipo de trabajo</span><strong>{jobTypeInfo?.label || form.job_type || '—'}</strong></div>
                  <div><span>Programación</span><strong>{scheduleSummary}</strong></div>
                  <div><span>Retención</span><strong>{form.retention_days ? `${form.retention_days} días` : '—'}</strong></div>
                  <div><span>Estado</span><strong>{statusLabel}</strong></div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'programacion' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Tipo de trabajo</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Qué hace este trabajo</label>
                    <select name="job_type" value={form.job_type} onChange={handleChange} className="form-select">
                      {jobTypes.map(j => <option key={j.value} value={j.value}>{j.label}</option>)}
                    </select>
                    {jobTypeInfo?.description && (
                      <div style={{ ...helpBox, marginTop: 6 }}>{jobTypeInfo.description}</div>
                    )}
                  </div>
                  {form.job_type === 'replica' && (
                    <div className="col-md-8">
                      <label className="form-label">Host donde se exponen las réplicas</label>
                      <select
                        value={repoValue(form.replica_host_type, form.replica_host_id)}
                        onChange={e => handleRepositoryChange('replica_host', e.target.value)}
                        className="form-select"
                      >
                        <option value="">— Sin enlazar —</option>
                        {repositoryTypes.map(rt => {
                          const items = repositories.filter(r => r.repository_type === rt.value);
                          if (!items.length) return null;
                          return (
                            <optgroup key={`rh-${rt.value}`} label={rt.label}>
                              {items.map(r => (
                                <option key={`rh-${r.repository_type}-${r.repository_id}`} value={`${r.repository_type}:${r.repository_id}`}>
                                  {r.name}{r.detail ? ` · ${r.detail}` : ''}
                                </option>
                              ))}
                            </optgroup>
                          );
                        })}
                      </select>
                      <div className="form-text" style={{ fontSize: '0.78rem' }}>
                        El host/standby donde las réplicas quedan registradas y pueden arrancar.
                      </div>
                    </div>
                  )}
                  {form.job_type === 'copy' && (
                    <>
                      <div className="col-md-4">
                        <label className="form-label">Cuándo se copia</label>
                        <select name="copy_trigger" value={form.copy_trigger} onChange={handleChange} className="form-select">
                          <option value="">—</option>
                          {copyTriggers.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">De qué trabajo copia</label>
                        <select name="source_policy_id" value={form.source_policy_id} onChange={handleChange} className="form-select">
                          <option value="">—</option>
                          {otherPolicies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Software y tipo de copia</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Software de backup</label>
                    <select name="software_service_id" value={form.software_service_id} onChange={handleChange} className="form-select">
                      <option value="">(sin software registrado)</option>
                      {backupSoftware.map(s => (
                        <option key={s.id} value={s.id}>{s.display_name || s.name}</option>
                      ))}
                      {softwareNotBackup && (
                        <option value={currentSoftware.id}>
                          {currentSoftware.display_name || currentSoftware.name} · no marcado como software de backup
                        </option>
                      )}
                    </select>
                    <div className="form-text" style={{ fontSize: '0.78rem' }}>
                      {backupSoftware.length === 0 ? (
                        <>Aquí solo salen servicios de tipo <strong>Software de backup</strong> y aún no hay ninguno.
                          Créalo en <Link to="/services">Servicios</Link> con el tipo «Software de backup» (Veeam, Acronis, Bacula…).</>
                      ) : (
                        <>Solo se ofrecen los servicios marcados como <strong>Software de backup</strong> en <Link to="/services">Servicios</Link>.</>
                      )}
                    </div>
                    {softwareNotBackup && (
                      <div className="form-text" style={{ ...helpBox, marginTop: 6 }}>
                        ⚠ Este servicio no está marcado como <strong>Software de backup</strong>, así que no aparece en el
                        desplegable. Cámbiale el tipo en <Link to="/services">Servicios</Link>.
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Tipo de backup</label>
                    <select name="backup_type" value={form.backup_type} onChange={handleChange} className="form-select">
                      <option value="">—</option>
                      {backupTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <div className="form-text" style={{ fontSize: '0.78rem' }}>
                      Opcional: clasificación de la copia (full, incremental, imagen…). El asistente guarda el
                      tipo de trabajo (copia, réplica o copia externa), no este campo.
                    </div>
                    {selectedType?.description && (
                      <div style={{ ...helpBox, marginTop: 6 }}>{selectedType.description}</div>
                    )}
                    <button
                      type="button"
                      className="dv-btn dv-btn-secondary"
                      style={{ marginTop: 6, padding: '2px 8px', fontSize: '0.78rem' }}
                      onClick={() => setShowTypeHelp(v => !v)}
                    >
                      <InformationCircleIcon />
                      {showTypeHelp ? 'Ocultar los tipos' : '¿Qué significa cada tipo?'}
                    </button>
                    {showTypeHelp && (
                      <div style={{ ...helpBox, marginTop: 6, display: 'grid', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
                        {backupTypes.map(t => (
                          <div key={t.value}>
                            <strong>{t.label}</strong>
                            <div style={{ opacity: 0.85 }}>{t.description}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Programación</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Frecuencia</label>
                    <select name="schedule_frequency" value={form.schedule_frequency} onChange={handleChange} className="form-select">
                      <option value="">(sin programar)</option>
                      {frequencies.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                  {form.schedule_frequency === 'interval' && (
                    <>
                      <div className="col-md-2">
                        <label className="form-label">Cada (horas)</label>
                        <input
                          name="schedule_interval_hours" value={form.schedule_interval_hours}
                          onChange={handleChange} className="form-control" type="number" min="1" max="24"
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Desde</label>
                        <input
                          name="schedule_window_start" value={form.schedule_window_start}
                          onChange={handleChange} className="form-control" type="time"
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Hasta</label>
                        <input
                          name="schedule_window_end" value={form.schedule_window_end}
                          onChange={handleChange} className="form-control" type="time"
                        />
                      </div>
                    </>
                  )}
                  {form.schedule_frequency !== 'custom' && form.schedule_frequency !== 'interval' && (
                    <div className="col-md-3">
                      <label className="form-label">Hora de inicio</label>
                      <input
                        name="schedule_time" value={form.schedule_time} onChange={handleChange}
                        className="form-control" type="time" disabled={!form.schedule_frequency}
                      />
                    </div>
                  )}
                  {form.schedule_frequency === 'monthly' && (
                    <div className="col-md-3">
                      <label className="form-label">Día del mes</label>
                      <input
                        name="schedule_day_of_month" value={form.schedule_day_of_month} onChange={handleChange}
                        className="form-control" type="number" min="1" max="31"
                      />
                    </div>
                  )}
                  {form.schedule_frequency === 'custom' && (
                    <div className="col-md-5">
                      <label className="form-label">Expresión cron</label>
                      <input name="schedule" value={form.schedule} onChange={handleChange} className="form-control" placeholder="0 2 * * *" />
                    </div>
                  )}

                  {form.schedule_frequency === 'weekly' && (
                    <div className="col-12">
                      <label className="form-label">Días de la semana</label>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {weekdays.map(d => {
                          const active = form.schedule_weekdays.includes(d.value);
                          return (
                            <button
                              key={d.value}
                              type="button"
                              title={d.label}
                              onClick={() => toggleWeekday(d.value)}
                              className={`dv-btn ${active ? 'dv-btn-primary' : 'dv-btn-secondary'}`}
                              style={{ minWidth: 44, padding: '4px 8px', justifyContent: 'center' }}
                            >
                              {WEEKDAY_SHORT[d.value] || d.label}
                            </button>
                          );
                        })}
                      </div>
                      <div className="form-text" style={{ fontSize: '0.78rem' }}>
                        Puedes marcar varios días (L M X J V S D = lunes a domingo).
                      </div>
                    </div>
                  )}

                  {form.schedule_frequency && form.schedule_frequency !== 'custom' && (
                    <div className="col-md-4">
                      <label className="form-label">Cron resultante</label>
                      <input value={cronPreview} className="form-control" readOnly placeholder="Se genera solo" />
                    </div>
                  )}

                  <div className="col-md-3">
                    <label className="form-label">Retención (días)</label>
                    <input name="retention_days" value={form.retention_days} onChange={handleChange} className="form-control" type="number" min="1" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Estado</label>
                    <select name="status" value={form.status} onChange={handleChange} className="form-select">
                      {(meta.statuses || ['active', 'disabled', 'error']).map(s => (
                        <option key={s} value={s}>{{ active: 'Activa', disabled: 'Desactivada', error: 'Con error' }[s] || s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'destino' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Repositorio (dónde aterrizan las copias)</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Tipo de destino</label>
                    <select name="destination_type" value={form.destination_type} onChange={handleChange} className="form-select">
                      <option value="">—</option>
                      {destinationOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-8">
                    <label className="form-label">Repositorio enlazado (CMDB)</label>
                    <select
                      value={repoValue(form.repository_type, form.repository_id)}
                      onChange={e => handleRepositoryChange('repository', e.target.value)}
                      className="form-select"
                    >
                      <option value="">— Sin enlazar (solo se guarda el detalle) —</option>
                      {repositoryTypes.map(rt => {
                        const items = repositories.filter(r => r.repository_type === rt.value);
                        if (!items.length) return null;
                        return (
                          <optgroup key={rt.value} label={rt.label}>
                            {items.map(r => (
                              <option key={`${r.repository_type}-${r.repository_id}`} value={`${r.repository_type}:${r.repository_id}`}>
                                {r.name}{r.is_cloud ? ' ☁' : ''}{r.detail ? ` · ${r.detail}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        );
                      })}
                    </select>
                    {(() => {
                      const repo = selectedRepository(form.repository_type, form.repository_id);
                      if (!repo) return null;
                      return (
                        <div style={{ ...helpBox, marginTop: 6 }}>
                          {repo.kind_label}{repo.detail ? ` · ${repo.detail}` : ''}
                          {repo.is_cloud ? ' · almacenamiento en nube' : ''}
                          {repo.hosting_type_label ? ` · ${repo.hosting_type_label}` : ''}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="col-12">
                    <label className="form-label">Detalle del destino (ruta, recurso o bucket)</label>
                    <input name="destination_detail" value={form.destination_detail} onChange={handleChange} className="form-control" placeholder="\\NAS-QNAP-01\backups  ·  /mnt/backup  ·  s3://bucket/prefijo" />
                    <div className="form-text" style={{ fontSize: '0.78rem' }}>
                      Los repositorios no se teclean: son elementos que ya están en la CMDB
                      (servidores y cabinas en <Link to="/infrastructure/servers">Infraestructura</Link>,
                      VMs en <Link to="/virt/vms">Virtualización</Link> y almacenamiento o nube en{' '}
                      <Link to="/services">Servicios</Link>). Si falta uno, dalo de alta ahí primero.
                    </div>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Cifrado y copia offsite</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="form-check mt-2">
                      <input className="form-check-input" type="checkbox" name="encryption" checked={form.encryption} onChange={handleChange} id="chk-encryption" />
                      <label className="form-check-label" htmlFor="chk-encryption">Cifrado en destino</label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check mt-2">
                      <input className="form-check-input" type="checkbox" name="is_offsite" checked={form.is_offsite} onChange={handleChange} id="chk-offsite" />
                      <label className="form-check-label" htmlFor="chk-offsite">Copia offsite</label>
                    </div>
                  </div>
                  <div className="col-md-8">
                    <label className="form-label">Repositorio offsite (nube o segunda ubicación)</label>
                    <select
                      value={repoValue(form.offsite_repository_type, form.offsite_repository_id)}
                      onChange={e => handleRepositoryChange('offsite_repository', e.target.value)}
                      className="form-select"
                    >
                      <option value="">— Sin enlazar —</option>
                      {repositoryTypes.map(rt => {
                        const items = repositories.filter(r => r.repository_type === rt.value);
                        if (!items.length) return null;
                        return (
                          <optgroup key={rt.value} label={rt.label}>
                            {items.map(r => (
                              <option key={`off-${r.repository_type}-${r.repository_id}`} value={`${r.repository_type}:${r.repository_id}`}>
                                {r.name}{r.is_cloud ? ' ☁' : ''}{r.detail ? ` · ${r.detail}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        );
                      })}
                    </select>
                    <div className="form-text" style={{ fontSize: '0.78rem' }}>
                      Al enlazar un repositorio offsite la política queda marcada como offsite.
                      Los servicios con hosting SaaS o tipo «Servicio de almacenamiento» se marcan con ☁.
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Detalle offsite</label>
                    <input name="offsite_detail" value={form.offsite_detail} onChange={handleChange} className="form-control" placeholder="bucket/backup" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'historial' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Historial de ejecución</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Último run</label>
                    <input name="last_run_at" value={form.last_run_at} onChange={handleChange} className="form-control" placeholder="2026-03-01T02:00:00" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Estado del último run</label>
                    <select name="last_run_status" value={form.last_run_status} onChange={handleChange} className="form-select">
                      <option value="">—</option>
                      {(meta.last_run_statuses || ['success','failed','partial','unknown']).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>

          {activeTab === 'targets' && isEdit && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Targets protegidos</div>
                {targets.length === 0 && <div className="dv-empty">Aún no hay targets</div>}
                {targets.length > 0 && (
                  <div className="dv-table-wrap">
                    <table className="dv-table">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th>Referencia</th>
                          <th>Notas</th>
                          {editing && <th></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {targets.map(t => {
                          const route = targetRoute(t);
                          return (
                            <tr key={t.id}>
                              <td>{targetTypeLabel(t.target_type)}</td>
                              <td>
                                {route ? (
                                  <Link to={route} className="dv-link">
                                    {t.target_name || `#${t.target_id}`}
                                  </Link>
                                ) : (t.target_name || `#${t.target_id}`)}
                              </td>
                              <td>{t.notes || '—'}</td>
                              {editing && (
                                <td>
                                  <button className="dv-icon-btn" type="button" onClick={() => deleteTarget(t.id)} title="Eliminar">
                                    <TrashIcon />
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              {editing && (
                <div className="dv-section">
                  <div className="dv-section-title">Añadir target</div>
                  <form className="dv-field-grid" onSubmit={addTarget}>
                    <label className="dv-field" style={{ gridColumn: '1 / span 2' }}>
                      <span>Qué se protege</span>
                      <select
                        value={targetForm.target_id ? `${targetForm.target_type}:${targetForm.target_id}` : ''}
                        onChange={e => {
                          const [type, id] = e.target.value ? e.target.value.split(':') : ['', ''];
                          setTargetForm(f => ({ ...f, target_type: type || 'server', target_id: id || '' }));
                        }}
                      >
                        <option value="">Selecciona el elemento</option>
                        {repositoryTypes.map(rt => {
                          const items = repositories.filter(r => r.repository_type === rt.value);
                          if (!items.length) return null;
                          return (
                            <optgroup key={rt.value} label={rt.label}>
                              {items.map(r => (
                                <option key={`t-${r.repository_type}-${r.repository_id}`} value={`${r.repository_type}:${r.repository_id}`}>
                                  {r.name}{r.detail ? ` · ${r.detail}` : ''}
                                </option>
                              ))}
                            </optgroup>
                          );
                        })}
                      </select>
                    </label>
                    <label className="dv-field" style={{ gridColumn: '1 / span 2' }}>
                      <span>Notas</span>
                      <input value={targetForm.notes} onChange={e => setTargetForm(f => ({ ...f, notes: e.target.value }))} />
                    </label>
                    <button className="dv-btn dv-btn-primary" type="submit" disabled={targetSaving} style={{ gridColumn: '1 / span 2' }}>
                      {targetSaving ? 'Añadiendo…' : <><PlusIcon /> Añadir target</>}
                    </button>
                  </form>
                </div>
              )}
              {!editing && <div className="dv-empty-notes">Pulsa «Editar» para añadir o quitar targets.</div>}
            </div>
          )}

          {activeTab === 'dependencias' && isEdit && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Servicio de backup y dependencias</div>
                <p className="dv-context-help">
                  Este mapa es el del servicio de backup enlazado: de dónde depende, qué ejecuta y qué protege.
                </p>
                {form.software_service_id ? (
                  <>
                    <div className="dv-context-actions" style={{ marginBottom: 10 }}>
                      <Link to={`/services/designer?service=${form.software_service_id}`} className="dv-btn dv-btn-secondary">
                        Abrir diseñador ↗
                      </Link>
                    </div>
                    <ServiceOverviewMap
                      serviceId={Number(form.software_service_id)}
                      companyId={companyId}
                      onOpenService={(id) => navigate(`/services?destacar=${id}&abrir=1`)}
                    />
                  </>
                ) : (
                  <div className="dv-empty-state">Selecciona un software de backup para ver sus dependencias.</div>
                )}
              </div>
            </div>
          )}
        </fieldset>
        <FormDirtyDock
          show={isCards && isDirty}
          saving={saving}
          onDiscard={handleDiscard}
          onSave={() => handleSave()}
        />
        </FormCardsShell>
        </div>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="backup_policy"
          ciId={policy?.id}
          tipoLegible="la política"
        />
      </div>
    </div>
  );
}
